import type { ChatMessage, ChatWithMessages } from '../api/chat-api';
import { ChatMessageOriginators } from '../enums/chat-message-originators';
import { MessageOriginators } from '../enums/message-originators';
import type { ChatMessageEntry } from '../types/chat.types';

export const ChatLogUtil = {
  /**
   * A conversation's messages as the log holds them.
   *
   * Ids are `${chatId}-${index}` rather than the message's own, because the scroll cues compare
   * against them and a re-read has to produce the same ones. Entries with no `messageOriginator`
   * are dropped - the wire types it nullish, and an entry that is neither side of the
   * conversation has nothing to render.
   */
  toEntries({
    chatId,
    messages,
    openAiFiles,
  }: {
    chatId: string;
    messages: ChatMessage[];
    openAiFiles: ChatWithMessages['openAiFiles'];
  }) {
    const spoken = messages.filter(
      (
        message
      ): message is ChatMessage & {
        messageOriginator: MessageOriginators;
      } => Boolean(message.messageOriginator)
    );

    return spoken.map((message, index): ChatMessageEntry => {
      const attachments = (message.fileIds ?? []).flatMap(fileId => {
        const fileName = openAiFiles?.[fileId]?.fileName;

        return fileName ? [{ fileId, fileName }] : [];
      });

      const hasAttachments = attachments.length > 0;

      return {
        attachments: hasAttachments ? attachments : undefined,
        chatId: hasAttachments ? chatId : undefined,
        /*
          `undefined` rather than `[]` when nobody matched: the restore control keys on the
          difference, and the wire says `null` for the same distinction.
        */
        matchingCandidates: message.matchingCandidates?.length
          ? message.matchingCandidates
          : undefined,
        id: `${chatId}-${index}`,
        messageOriginator:
          message.messageOriginator === MessageOriginators.LLM
            ? ChatMessageOriginators.LLM
            : ChatMessageOriginators.USER,
        text: message.text ?? '',
      };
    });
  },

  /** The talent the newest answer put forward, for the grid to open on. */
  latestMatchingCandidates({ messages }: { messages: ChatMessage[] }) {
    return (
      messages.findLast(
        message =>
          message.messageOriginator === MessageOriginators.LLM &&
          // The wire sends `null`, not `[]`, for an answer that matched nobody.
          (message.matchingCandidates?.length ?? 0) > 0
      )?.matchingCandidates ?? []
    );
  },

  /** The newest answer's id, which the next send has to quote or the server 409s it. */
  latestAnswerId({ messages }: { messages: ChatMessage[] }) {
    return (
      messages.findLast(
        message => message.messageOriginator === MessageOriginators.LLM
      )?.id ?? null
    );
  },
};
